import {
	FieldMappingSourceType,
	MsdynamicsEntityType,
	MsdynamicsField,
	MsdynamicsSourceField,
} from "../../schema/dynamics";
import {
	MsdynamicsFieldMappingPreviewResponse,
	MsdynamicsTestSyncResponse,
} from "../../schema/dynamicsMappingPreview";
import {
	MappingPreviewLabels,
	MappingPreviewOutcome,
	MappingTestSyncOutcome,
} from "./MappingPreviewModal";

type Ts = (key: string, options?: Record<string, unknown>) => string;

/** i18n key of the tab label shown as the "which mapping am I previewing" badge. */
const ENTITY_BADGE_KEY: Record<MsdynamicsEntityType, string> = {
	[MsdynamicsEntityType.CONTACT]: "contacts",
	[MsdynamicsEntityType.ACCOUNT]: "accounts",
	[MsdynamicsEntityType.LEAD]: "leads",
	[MsdynamicsEntityType.OPPORTUNITY]: "opportunities",
	[MsdynamicsEntityType.CAMPAIGN_RESPONSE]: "campaignResponses",
	[MsdynamicsEntityType.CAMPAIGN]: "campaigns",
	[MsdynamicsEntityType.MARKETING_LIST]: "marketingLists",
};

/** Labels for the preview modal, read from the Dynamics `fieldMapping` i18n block. */
export const buildDynamicsMappingPreviewLabels = (
	ts: Ts,
	entityType: MsdynamicsEntityType
): MappingPreviewLabels => ({
	entityBadge: ts(ENTITY_BADGE_KEY[entityType]),
	createsLabel: ts("preview.createsLabel"),
	createsDescription: ts(`preview.createsByEntity.${entityType}`),
	cancel: ts("preview.cancel"),
	title: ts("preview.title"),
	subtitle: ts("preview.subtitle"),
	prospectLabel: ts("preview.prospectLabel"),
	prospectPlaceholder: ts("preview.prospectPlaceholder"),
	run: ts("preview.run"),
	source: ts("ourField"),
	target: ts("msdynamicsField"),
	value: ts("preview.value"),
	emptyValue: ts("preview.emptyValue"),
	aiNotRun: ts("preview.aiNotRun"),
	inactive: ts("preview.inactive"),
	fallbacksTitle: ts("preview.fallbacksTitle"),
	fallbacksHint: ts("preview.fallbacksHint"),
	noMappings: ts("preview.noMappings"),
	error: ts("preview.error"),
	missingRequiredTitle: ts("preview.warnings.missingRequiredTitle"),
	missingRequiredHint: ts("preview.warnings.missingRequiredHint"),
	syncButton: ts("preview.sync.button"),
	syncWarning: ts(`preview.sync.warningByEntity.${entityType}`),
	syncSuccessTitle: ts("preview.sync.successTitle"),
	syncFailedTitle: ts("preview.sync.failedTitle"),
	syncOpenRecord: ts("preview.sync.openRecord"),
});

/**
 * Map the Dynamics preview response to display rows.
 *
 * Unlike the Zoho binding, no value validation happens here: Dataverse is strictly typed
 * and the API already ran the sync's own coercion pass, so `skipReason` is the authoritative
 * "this value will not be written" verdict — reproducing that rule in the browser could
 * only disagree with the sync.
 */
export const buildDynamicsPreviewOutcome = (
	response: MsdynamicsFieldMappingPreviewResponse,
	sourceFields: MsdynamicsSourceField[],
	msdynamicsFields: MsdynamicsField[],
	ts: Ts
): MappingPreviewOutcome => {
	const targetLabel = (msdynamicsField: string, storedLabel?: string) =>
		msdynamicsFields.find((field) => field.name === msdynamicsField)?.label ??
		storedLabel ??
		msdynamicsField;

	const sourceLabel = (sourceType: FieldMappingSourceType, sourceField: string) => {
		if (sourceType === FieldMappingSourceType.CONSTANT) {
			return ts("constantLabel", { value: sourceField });
		}

		if (sourceType === FieldMappingSourceType.AI) {
			return sourceField;
		}

		return sourceFields.find((field) => field.name === sourceField)?.label ?? sourceField;
	};

	return {
		rows: response.rows.map((row) => ({
			id: row.mappingId,
			sourceLabel: sourceLabel(row.sourceType, row.sourceField),
			targetLabel: targetLabel(row.msdynamicsField, row.msdynamicsFieldLabel),
			value: row.value,
			resolved: row.resolved,
			isAiMapping: row.isAiMapping,
			isActive: row.isActive,
			warnings: [row.skipReason, row.emptyReason].filter((reason): reason is string => !!reason),
		})),
		fallbacks: response.fallbacks.map((fallback) => ({
			targetLabel: targetLabel(fallback.msdynamicsField),
			value: fallback.value,
		})),
		missingRequired: response.missingRequired,
	};
};

/** Map the Dynamics test-sync response to display records for the preview modal. */
export const buildDynamicsTestSyncOutcome = (
	response: MsdynamicsTestSyncResponse,
	ts: Ts
): MappingTestSyncOutcome => {
	// On an opportunity or lead test the deep link points at that record, not the contact.
	const isDealRecord = Boolean(response.msdynamicsOpportunityId || response.msdynamicsLeadId);
	const contactUrl = isDealRecord ? null : (response.recordUrl ?? null);
	const records: MappingTestSyncOutcome["records"] = [];

	if (response.msdynamicsContactId) {
		records.push({
			label: ts("preview.sync.contactRecord", { id: response.msdynamicsContactId }),
			url: contactUrl,
		});
	}

	if (response.msdynamicsAccountId) {
		records.push({
			label: ts("preview.sync.accountRecord", { id: response.msdynamicsAccountId }),
			url: null,
		});
	}

	if (response.msdynamicsLeadId) {
		records.push({
			label: ts("preview.sync.leadRecord", { id: response.msdynamicsLeadId }),
			url: response.recordUrl ?? null,
		});
	}

	if (response.msdynamicsOpportunityId) {
		records.push({
			label: ts("preview.sync.opportunityRecord", { id: response.msdynamicsOpportunityId }),
			url: response.recordUrl ?? null,
		});
	}

	return { success: response.success, error: response.error ?? null, records };
};
